// Marketeurs, as Ruben chose on 2 Nov 2026 from the eight round-two designs:
// « L'essentiel » on top (three numbers, three sentences, every brand), the
// pilot zones on the real map, « Le classement » (a calm list whose rows open),
// and « Vue marketeur » (the page each brand will see, beside the 5-home rule).
// Data: ops_marketers and ops_brand_view (migration 0047), logos and bottle
// pictures from the catalogue (0048). Brands see counts only, never a home.
import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { LANG, S } from '../lib/strings';
import { TZ, fmtN } from '../lib/format';
import type { Source } from '../lib/api';
import type { BrandRow, BrandView, Marketers as Mk } from '../lib/types';
import { brandQuartiers, countWord, cover, displayName, emptyDays, refillSplit, shareWords, shortBrands, zonesOf } from '../lib/brands';
import { BrandCyl } from '../components/Bottles';
import { Ic } from '../components/Icon';
import { Wordmark } from '../components/Wordmark';
import { ZoneMap, type ZoneBrand } from '../components/ZoneMap';
import { Loading } from '../components/Loading';

const REDUCE = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
const DAY = 86_400_000;
const pct = (a: number, b: number) => (b ? Math.round((a / b) * 100) : 0);
const pad = (n: number) => String(n).padStart(2, '0');
const when = (i: number, o: Intl.DateTimeFormatOptions) =>
  new Date(Date.now() + i * DAY).toLocaleDateString(LANG === 'en' ? 'en-GB' : 'fr-FR', { ...o, timeZone: TZ }).replace(/\.$/, '');
const sunday = () => when(6, { weekday: 'long' });
const sundayFull = () => when(6, { weekday: 'long', day: 'numeric', month: 'long' });
const mondayOf = () => when(-((new Date().getDay() + 6) % 7), { day: 'numeric', month: 'long', year: 'numeric' });
const andList = (xs: string[]) => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} ${LANG === 'en' ? 'and' : 'et'} ${xs[xs.length - 1]}`);
const signed = (d: number) => (d > 0 ? '+' + d : d < 0 ? '−' + -d : '=');
const go = (el: HTMLElement | null) => el?.scrollIntoView({ behavior: REDUCE ? 'auto' : 'smooth', block: 'start' });

type Brand = BrandRow & { label: string };

/** The brand's own logo on a white tile (logos are drawn for white); its name if there is no picture. */
function Logo({ b, size = 'm' }: { b: { label: string; logo?: string | null }; size?: 's' | 'm' | 'l' }) {
  const [bad, setBad] = useState(false);
  return (
    <span className={'mq-logo ' + size}>
      {b.logo && !bad ? <img src={b.logo} alt={b.label} loading="lazy" onError={() => setBad(true)} /> : <span className="tx">{b.label}</span>}
    </span>
  );
}

/** The brand's 12.5 kg bottle in its paint; the drawn one when the catalogue has no picture. */
function Bot({ b, h = 28 }: { b: { brand_color: string; bottle?: string | null }; h?: number }) {
  const [bad, setBad] = useState(false);
  return b.bottle && !bad
    ? <img className="mq-bot" src={b.bottle} alt="" aria-hidden="true" loading="lazy" style={{ height: h, width: h / 2 }} onError={() => setBad(true)} />
    : <span className="mq-bot"><BrandCyl color={b.brand_color} h={h} /></span>;
}

function CoverPill({ stock, due }: { stock: number; due: number }) {
  const c = cover(stock, due);
  if (c === 'none') return <span className="mq-none">{S.mk.nothingToPlan}</span>;
  return c === 'ok' ? <span className="st green">{S.mk.covered}</span> : <span className="st red">{S.mk.missing(due - stock)}</span>;
}

/** Eight weeks of refills, grey, the last point marked; numbers under each point. */
function Spark({ w }: { w: number[] }) {
  const W = 300, H = 64, mx = Math.max(1, ...w);
  const x = (i: number) => 10 + (i * (W - 20)) / 7;
  const y = (v: number) => 8 + (1 - v / mx) * (H - 16);
  return (
    <svg className="mq-spark" viewBox={`0 0 ${W} ${H + 18}`} role="img" aria-label={S.mk.perWeek}>
      <line className="gl" x1="0" x2={W} y1={H - 8} y2={H - 8} />
      <polyline fill="none" className="ln" points={w.map((v, i) => `${x(i)},${y(v)}`).join(' ')} />
      {w.map((v, i) => <circle key={i} cx={x(i)} cy={y(v)} r={i === 7 ? 4 : 2.6} className={i === 7 ? 'end' : 'pt'} />)}
      {w.map((v, i) => <text key={i} x={x(i)} y={H + 14} textAnchor="middle" className={'ax' + (i === 7 ? ' on' : '')}>{v}</text>)}
    </svg>
  );
}

function Empty() {
  return <section className="card empty" style={{ marginTop: 8 }}><b>{S.mk.noData}</b><span>{S.mk.noDataSub}</span></section>;
}

export function Marketers({ src, refresh }: { src: Source; refresh: Date | null }) {
  const [mk, setMk] = useState<Mk | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [view, setView] = useState<'admin' | 'brand'>('admin');
  const [brand, setBrand] = useState<string | null>(null);
  const [bv, setBv] = useState<BrandView | null>(null);
  const [sort, setSort] = useState<'n' | 'level' | 'due' | 'refills'>('n');
  const [shortOnly, setShortOnly] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const zonesRef = useRef<HTMLElement>(null);
  const rankRef = useRef<HTMLElement>(null);

  useEffect(() => {
    let live = true;
    src.marketers().then((x) => { if (live) { setMk(x); setErr(null); } }).catch((e) => { if (live) setErr(String(e?.message ?? e)); });
    return () => { live = false; };
  }, [src, refresh]);

  const all: Brand[] = useMemo(() => (mk?.brands ?? []).map((b) => ({ ...b, label: displayName(b.code, b.name) })), [mk]);
  const brands = useMemo(() => all.filter((b) => b.stats.n > 0).sort((a, b) => b.stats.n - a.stats.n || a.label.localeCompare(b.label)), [all]);
  const byCode = useMemo(() => new Map(all.map((b) => [b.code, b])), [all]);
  const zones = useMemo(() => zonesOf(mk?.bottles ?? []), [mk]);
  const zb = useMemo(() => Object.fromEntries(all.map((b) => [b.code, { name: b.label, logo: b.logo } satisfies ZoneBrand])), [all]);
  const shown = view === 'brand' ? brand ?? brands[0]?.code ?? null : null;

  useEffect(() => {
    if (!shown) return;
    let live = true;
    src.brandView(shown).then((x) => { if (live) setBv(x); }).catch(() => { if (live) setBv(null); });
    return () => { live = false; };
  }, [src, shown, refresh]);

  if (err && !mk) return <section className="view on"><div className="card empty" style={{ marginTop: 24 }}><b>{S.errors.load}</b><span className="mono">{err}</span></div></section>;
  if (!mk) return <Loading />;

  const adm = view === 'admin';
  const total = brands.reduce((a, b) => a + b.stats.n, 0);
  const due = brands.reduce((a, b) => a + b.stats.due7, 0);
  const sh = shortBrands(brands);
  const openBrand = (code: string) => { setView('brand'); setBrand(code); window.scrollTo({ top: 0 }); };

  return (
    <section className="view on mq" aria-label={S.mk.title}>
      <div className="ph mk-ph">
        <div><h1>{S.mk.title}</h1><p>{adm ? S.mk.sub : S.mk.subBrand}</p></div>
        <div className="r">
          <div className="seg" role="group" aria-label={S.mk.who}>
            <button aria-pressed={adm} onClick={() => setView('admin')}><Ic n="grid" s="xs" />{S.mk.admin}</button>
            <button aria-pressed={!adm} disabled={!brands.length} onClick={() => setView('brand')}><Ic n="eye" s="xs" />{S.mk.brand}</button>
          </div>
        </div>
      </div>

      {!brands.length ? <Empty /> : adm ? (
        <>
          {/* 1 · three numbers */}
          <section className="card mq-kpis" aria-label={S.mk.title}>
            <div><span className="cap">{S.mk.kBottles}</span><b className="num">{fmtN(total)}</b><p>{S.mk.kBottlesSub(brands.length)}</p></div>
            <div><span className="cap">{S.mk.kDue}</span><b className="num">{fmtN(due)}</b><p>{S.mk.kDueSub(pct(due, total), sundayFull())}</p></div>
            <div><span className="cap">{sh.short.length > 0 && <i className="mq-dot" aria-hidden="true" />}{S.mk.kShort}</span><b className="num">{sh.short.length}</b><p>{S.mk.kShortSub(sh.missing)}</p></div>
          </section>

          {/* 2 · three sentences */}
          <div className="mq-h"><h2>{S.mk.retain}</h2><span>{S.mk.retainSub}</span></div>
          <div className="mq-says">
            <Lead brands={brands} total={total} zones={zones} byCode={byCode} onSee={() => go(zonesRef.current)} />
            <Refills brands={brands} onSee={() => { setSort('refills'); setShortOnly(false); go(rankRef.current); }} />
            <Short sh={sh} due={due} onSee={() => { setShortOnly(true); setOpen(null); go(rankRef.current); }} />
          </div>

          {/* 3 · every brand */}
          <div className="mq-h"><h2>{S.mk.strip(brands.length)}</h2><span>{S.mk.stripSub}</span></div>
          <section className="card mq-strip" style={{ ['--cols' as string]: Math.min(brands.length, 13) }}>
            {brands.map((b) => (
              <button key={b.code} type="button" onClick={() => { setShortOnly(false); setOpen(b.code); setTimeout(() => go(document.getElementById('mq-row-' + b.code)), 0); }} aria-label={`${b.label} · ${b.stats.n}`}>
                <Logo b={b} size="s" />
                <span className="mq-sn"><Bot b={b} h={26} /><b className="num">{b.stats.n}</b><em className="num">{pct(b.stats.n, total)} %</em></span>
              </button>
            ))}
          </section>

          {/* 4 · the pilot zones */}
          <section ref={zonesRef} className="mq-sec" aria-label={S.mk.zones}>
            <div className="mq-h"><h2>{S.mk.zones}</h2><span>{S.mk.zonesSub}</span></div>
            <div className="mq-zones">
              <div className="card mq-map"><ZoneMap zones={zones} brands={zb} onZone={(n) => go(document.getElementById('mq-zone-' + n))} /></div>
              <div className="mq-zcards">
                {zones.map((z) => {
                  const L = z.leader ? byCode.get(z.leader) : undefined;
                  return (
                    <article key={z.name} id={'mq-zone-' + z.name} className="card mq-zc">
                      <header><h3>{z.name}</h3><span>{S.mk.homes(z.n)} · {S.mk.brandsN(z.counts.length)}</span></header>
                      <div className="mq-zl">
                        {L ? <><Logo b={L} size="m" /><p><b>{L.label}</b>{S.mk.leadsTail(z.lead, z.n)}</p></> : <><span className="st">{S.mk.noLeader}</span><p>{S.mk.tieSub(z.counts.length)}</p></>}
                      </div>
                      <div className="mq-chips">
                        {z.counts.filter((c) => c.code !== z.leader).map((c) => { const b = byCode.get(c.code); return b ? <span key={c.code} className="mq-chip"><Bot b={b} h={18} /><Logo b={b} size="s" /><b className="num">{c.n}</b></span> : null; })}
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </section>

          {/* 5 · the ranking */}
          <Rank refEl={rankRef} brands={brands} all={all} total={total} mk={mk} sort={sort} setSort={setSort} shortOnly={shortOnly} setShortOnly={setShortOnly} open={open} setOpen={setOpen} shortN={sh.short.length} onView={openBrand} />
        </>
      ) : (
        <BrandPage brands={brands} q={q} setQ={setQ} shown={shown} setBrand={setBrand} bv={bv} mk={mk} zones={zones} byCode={byCode} />
      )}
    </section>
  );
}

function Lead({ brands, total, zones, byCode, onSee }: { brands: Brand[]; total: number; zones: ReturnType<typeof zonesOf>; byCode: Map<string, Brand>; onSee(): void }) {
  const [a, b] = brands;
  const n = a.stats.n + (b?.stats.n ?? 0);
  const pair = [a, b].filter((x): x is Brand => !!x);
  const leads = pair.flatMap((x) => zones.filter((z) => z.leader === x.code).slice(0, 1).map((z) => ({ x, z })));
  const body = leads.length ? leads.map(({ x, z }, i) => S.mk.leadsIn(x.label, z.name, z.lead, z.n, i === 0)).join(', ') + '.' : S.mk.leadNone;
  return (
    <article className="card mq-say">
      <div className="mq-logos">{pair.map((x) => <Logo key={x.code} b={byCode.get(x.code) ?? x} />)}</div>
      <h3>{b ? S.mk.lead2(a.label, b.label, shareWords(pct(n, total), LANG)) : S.mk.lead1(a.label)}</h3>
      <p>{b ? S.mk.leadBody(n, total) + ' ' : ''}{body}</p>
      <footer><button className="mq-link" onClick={onSee}>{S.mk.seeWho}<Ic n="chev" s="xs" /></button></footer>
    </article>
  );
}

function Refills({ brands, onSee }: { brands: Brand[]; onSee(): void }) {
  const w = Array(8).fill(0);
  brands.forEach((b) => b.stats.weeks8.forEach((v, i) => { w[i] += v; }));
  const before = w.slice(0, 4).reduce((x, y) => x + y, 0), last = w.slice(4).reduce((x, y) => x + y, 0);
  const p = before ? Math.round(((last - before) / before) * 100) : 0;
  const up = last >= before;
  const movers = brands.map((b) => ({ b, d: refillSplit(b.stats).delta })).filter((m) => (up ? m.d > 0 : m.d < 0)).sort((x, y) => Math.abs(y.d) - Math.abs(x.d)).slice(0, 2);
  const title = !before && !last ? S.mk.noRefill : !before ? S.mk.fresh(last) : Math.abs(p) < 5 ? S.mk.flat : up ? S.mk.up(p) : S.mk.down(-p);
  return (
    <article className="card mq-say">
      <div className="mq-logos">
        {movers.map((m) => <Logo key={m.b.code} b={m.b} />)}
        <svg className="mq-mini" viewBox="0 0 120 34" aria-hidden="true">
          <polyline fill="none" points={w.map((v, i) => `${4 + i * 16},${30 - (v / Math.max(1, ...w)) * 24}`).join(' ')} />
          <circle cx={4 + 7 * 16} cy={30 - (w[7] / Math.max(1, ...w)) * 24} r="3" />
        </svg>
      </div>
      <h3>{title}</h3>
      <p>{before || last ? S.mk.refBody(last, before) : ''}{movers.length && before ? ' ' + S.mk.drive(andList(movers.map((m) => `${m.b.label} (${signed(m.d)})`)), movers.length === 1, up) : ''}</p>
      <footer><button className="mq-link" onClick={onSee}>{S.mk.seeRefills}<Ic n="chev" s="xs" /></button></footer>
    </article>
  );
}

function Short({ sh, due, onSee }: { sh: ReturnType<typeof shortBrands<Brand>>; due: number; onSee(): void }) {
  const k = sh.short.length;
  return (
    <article className="card mq-say">
      <div className="mq-logos">{sh.short.slice(0, 4).map((b) => <Logo key={b.code} b={b} />)}{k > 4 && <span className="mq-more">+{k - 4}</span>}</div>
      <h3>{k ? S.mk.short(countWord(k, LANG), k) : S.mk.allCovered}</h3>
      <p>{k ? S.mk.shortBody(sh.due, sh.stock, sunday(), k === 1) : S.mk.coveredBody(due, sunday())}</p>
      {k > 0 && <footer><button className="btn sm" onClick={onSee}>{S.mk.seeShort}</button></footer>}
    </article>
  );
}

interface RankProps {
  refEl: RefObject<HTMLElement | null>; brands: Brand[]; all: Brand[]; total: number; mk: Mk;
  sort: 'n' | 'level' | 'due' | 'refills'; setSort(s: 'n' | 'level' | 'due' | 'refills'): void;
  shortOnly: boolean; setShortOnly(v: boolean): void; open: string | null; setOpen(c: string | null): void; shortN: number; onView(code: string): void;
}

function Rank({ refEl, brands, all, total, mk, sort, setSort, shortOnly, setShortOnly, open, setOpen, shortN, onView }: RankProps) {
  const key = (b: Brand) => (sort === 'n' ? -b.stats.n : sort === 'level' ? b.stats.level : sort === 'due' ? -b.stats.due7 : -refillSplit(b.stats).total);
  const rows = brands.filter((b) => !shortOnly || cover(b.stock, b.stats.due7) === 'short').sort((a, b) => key(a) - key(b) || b.stats.n - a.stats.n);
  const empty = all.filter((b) => !b.stats.n);
  const top = brands[0];
  return (
    <section ref={refEl} className="mq-sec" aria-label={S.mk.rank}>
      <div className="mq-h"><h2>{S.mk.rank}</h2><span>{S.mk.rankSub}</span></div>
      <p className="mq-sum"><b>{S.mk.rankSum(brands.length, total)}</b>{top && S.mk.rankLead(top.label, top.stats.n)}{S.mk.rankShort(shortN)}</p>
      <div className="mq-tools">
        <div className="seg" role="group" aria-label={S.mk.sortBy}>
          {(['n', 'level', 'due', 'refills'] as const).map((k) => <button key={k} aria-pressed={sort === k} onClick={() => setSort(k)}>{S.mk.sorts[k]}</button>)}
        </div>
        {shortN > 0 && <button className={'mq-filter' + (shortOnly ? ' on' : '')} aria-pressed={shortOnly} onClick={() => setShortOnly(!shortOnly)}><i className="mq-dot" aria-hidden="true" />{S.mk.shortOnly(shortN)}{shortOnly && <Ic n="x" s="xs" />}</button>}
      </div>
      <div className="card mq-rank">
        <div className="mq-tr mq-th" aria-hidden="true">{S.mk.cols.map((c) => <span key={c}>{c}</span>)}<span /></div>
        {rows.map((b, i) => {
          const isOpen = open === b.code;
          const r = refillSplit(b.stats);
          return (
            <div key={b.code} id={'mq-row-' + b.code} className={'mq-row' + (isOpen ? ' open' : '')}>
              <button className="mq-tr" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : b.code)}>
                <span className="mq-n num">{pad(i + 1)}</span>
                <span className="mq-who"><Bot b={b} h={30} /><Logo b={b} size="m" /><b>{b.label}</b></span>
                <span className="mq-c" data-l={S.mk.cols[2]}><b className="num">{b.stats.n}</b><em className="num">{pct(b.stats.n, total)} %</em></span>
                <span className="mq-c mq-lv" data-l={S.mk.cols[3]}><span className={'mq-bar' + (b.stats.level < 35 ? ' low' : '')}><i style={{ width: b.stats.level + '%' }} /></span><span className="num">{b.stats.level} %</span></span>
                <span className={'mq-c num' + (b.stats.due7 ? '' : ' mute')} data-l={S.mk.cols[4]}>{b.stats.due7}</span>
                <span className="mq-c mq-stk" data-l={S.mk.cols[5]}><b className="num">{b.stock}</b><CoverPill stock={b.stock} due={b.stats.due7} /></span>
                <span className="mq-c" data-l={S.mk.cols[6]}><b className="num">{r.total}</b><em className={'num ' + (r.delta > 0 ? 'up' : r.delta < 0 ? 'dn' : '')}>{signed(r.delta)}</em></span>
                <span className="mq-chev"><Ic n={isOpen ? 'chevd' : 'chev'} s="xs" /></span>
              </button>
              {isOpen && <RowDetail b={b} mk={mk} onView={() => onView(b.code)} />}
            </div>
          );
        })}
        {!rows.length && <div className="mq-nobody">{S.mk.allCovered}</div>}
      </div>
      {empty.length > 0 && <p className="mq-foot">{S.mk.noBottle(empty.map((b) => b.label).join(', '))}</p>}
    </section>
  );
}

function RowDetail({ b, mk, onView }: { b: Brand; mk: Mk; onView(): void }) {
  const r = refillSplit(b.stats);
  const bq = brandQuartiers(mk.bottles, b.code, mk.k);
  const mx = Math.max(1, ...bq.rows.map((x) => x.n));
  const days = emptyDays(b.stats.d7).map((i) => when(i, { weekday: 'short', day: 'numeric' }));
  return (
    <div className="mq-detail">
      <div className="mq-box"><span className="cap">{S.mk.perWeek}</span><Spark w={b.stats.weeks8} /><p>{S.mk.perWeekSub(r.total, r.delta)}</p></div>
      <div className="mq-box"><span className="cap">{S.mk.byQ}</span>
        <div className="mq-qbars">{bq.rows.map((x) => <div key={x.q}><span>{x.q}</span><span className="mq-bar"><i style={{ width: (x.n / mx) * 100 + '%' }} /></span><b className="num">{x.n}</b></div>)}</div>
      </div>
      <div className="mq-box"><span className="cap">{S.mk.thisWeek}</span>
        {b.stats.due7 ? <><p className="mq-big"><b className="num">{b.stats.due7}</b> {S.mk.toReplace}</p><p>{andList(days)}</p></> : <p className="mq-big sm">{S.mk.noneDue}</p>}
        <div className="mq-cov"><CoverPill stock={b.stock} due={b.stats.due7} /><span>{S.mk.fullInStation(b.stock)}</span></div>
      </div>
      <div className="mq-box"><span className="cap">{S.mk.sees(b.label)}</span>
        <p className="mq-big sm">{S.mk.seesTitle(b.stats.n)}</p>
        <p>{bq.shown.length ? S.mk.seesSome(andList(bq.shown.map((x) => x.q))) : S.mk.seesNone(mk.k, b.label)}</p>
        <button className="btn sm" onClick={onView}><Ic n="eye" s="xs" />{S.mk.openView(b.label)}</button>
      </div>
    </div>
  );
}

interface PageProps {
  brands: Brand[]; q: string; setQ(v: string): void; shown: string | null; setBrand(c: string): void;
  bv: BrandView | null; mk: Mk; zones: ReturnType<typeof zonesOf>; byCode: Map<string, Brand>;
}

function BrandPage({ brands, q, setQ, shown, setBrand, bv, mk, zones, byCode }: PageProps) {
  const sel = shown ? byCode.get(shown) : undefined;
  const list = brands.filter((b) => !q || b.label.toLowerCase().includes(q.toLowerCase()));
  const v = bv && bv.code === shown ? bv : null;
  const label = sel?.label ?? '';
  const bq = sel ? brandQuartiers(mk.bottles, sel.code, mk.k) : null;
  const anyShown = zones.filter((z) => z.counts.some((c) => c.n >= mk.k)).length;
  const r = v ? refillSplit(v.stats) : null;
  return (
    <div className="mq-page">
      <aside className="card mq-list" aria-label={S.mk.title}>
        <div className="mq-lh"><b>{S.mk.title}</b><span className="num">{brands.length}</span></div>
        <label className="mq-find"><Ic n="search" s="xs" /><input id="mq-find" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={S.mk.search} aria-label={S.mk.search} /></label>
        <div className="mq-lb">
          {list.map((b) => (
            <button key={b.code} aria-pressed={b.code === shown} onClick={() => setBrand(b.code)}>
              <Bot b={b} h={24} /><Logo b={b} size="s" /><span className="mq-ln">{b.label}</span><b className="num mq-lc">{b.stats.n}</b>
            </button>
          ))}
        </div>
      </aside>

      <div className="mq-center">
        <div className="mq-ch"><div><h2>{S.mk.space(label)}</h2><p>{S.mk.spaceSub}</p></div></div>
        <section className="card mq-frame" aria-label={S.mk.space(label)}>
          {!v || !sel ? <div className="loading-dot" /> : (
            <>
              <header className="mq-fh"><Logo b={sel} size="l" /><span className="mq-by">{S.mk.frame}<Wordmark /></span></header>
              <h3>{S.mk.yourFleet}</h3>
              <p className="mq-fs">{S.mk.yourSub(mondayOf())}</p>
              <div className="mq-cells">
                <div><span className="cap">{S.mk.cells.n}</span><b className="num">{v.stats.n}</b><p>{S.mk.cells.nSub}</p></div>
                <div><span className="cap">{S.mk.cells.level}</span><b className="num">{v.stats.level} %</b><p>{S.mk.cells.levelSub}</p></div>
                <div><span className="cap">{S.mk.cells.due}</span><b className="num">{v.stats.due7}</b><p>{S.mk.cells.dueSub(sunday())}</p></div>
                <div><span className="cap">{S.mk.cells.stock}</span><b className="num">{v.stock}</b><p>{S.mk.cells.stockSub}</p></div>
                <div><span className="cap">{S.mk.cells.refills}</span><b className="num">{r?.total ?? 0}</b><p className={r && r.delta > 0 ? 'up' : ''}>{S.mk.cells.refillsSub(r?.delta ?? 0)}</p></div>
                <div><span className="cap">{S.mk.when}</span>
                  <div className="mq-bands">{(['week', 'under_month', 'month'] as const).map((k) => <span key={k}><b className="num">{v.bands[k]}</b>{S.mk.bands[k]}</span>)}</div>
                </div>
              </div>
              <div className="mq-qt">
                <div className="mq-qth"><span className="cap">{S.mk.byQ}</span><span className="cap">{S.mk.homesCol}</span></div>
                {v.quartiers.map((x) => <div key={x.quartier} className="mq-qtr"><span>{x.quartier}</span><b className="num">{x.n}</b></div>)}
                {v.others.n > 0 && <div className="mq-qtr"><span><Ic n="lock" s="xs" />{S.mk.others}</span><b className="num">{v.others.n}</b></div>}
              </div>
              <p className="mq-lock"><Ic n="lock" s="xs" />{S.mk.lockNote(v.k, label)}</p>
            </>
          )}
        </section>
      </div>

      <aside className="card mq-rule">
        <span className="cap mq-blue">{S.mk.rule(mk.k)}</span>
        <h3>{S.mk.ruleT(label)}</h3>
        <p>{S.mk.ruleBody(mk.k)}</p>
        {bq && (
          <div className="mq-rt">
            <div className="mq-qth"><span className="cap">{S.mk.mgView}</span><span className="cap">{S.mk.homesCol}</span></div>
            {bq.rows.map((x) => <div key={x.q} className="mq-qtr"><span>{x.q}</span><em>{x.n >= mk.k ? S.mk.visible : S.mk.grouped}</em><b className="num">{x.n}</b></div>)}
          </div>
        )}
        {v && v.others.n > 0 && <div className="mq-sees"><span>{S.mk.brandSees(label)}</span><b className="num">{v.others.n}</b></div>}
        {bq?.next && (
          <div className="mq-next">
            <p>{S.mk.firstZone(bq.next.q, bq.next.n, mk.k, label)}</p>
            <div className="mq-steps" aria-hidden="true">{Array.from({ length: mk.k }, (_, i) => <i key={i} className={i < bq.next!.n ? 'on' : ''} />)}</div>
          </div>
        )}
        <p className="mq-note">{anyShown ? S.mk.someYet(anyShown, mk.k) : S.mk.noneYet(mk.k)}</p>
        <p className="mq-never"><b>{S.mk.never}</b>{LANG === 'fr' ? ' : ' : ': '}{S.mk.neverList}</p>
      </aside>
    </div>
  );
}
